/**
 * Config-driven form validation for KYB submit.
 * Replaces hardcoded required arrays with resolveFieldConfig + dependsOn.
 */
import { evaluateCondition } from "./conditionUtils";
import { resolveFieldConfig } from "./index";
import type {
	FieldConfig,
	FieldResolverContext,
	ValidationRule,
} from "./types";

function filterByDependsOn(
	fields: FieldConfig[],
	formValues: Record<string, unknown>,
): FieldConfig[] {
	return fields.filter((field) => {
		if (!field.dependsOn) return true;
		return evaluateCondition(field.dependsOn, formValues);
	});
}

/** Known composite/object field IDs that need special isFilled logic */
const CONTACT_FIELDS = new Set([
	"primaryBusinessContact",
	"primaryProductContact",
	"primaryLegalContact",
	"primarySigner",
	"complianceOfficer",
]);

/** registeredAddress sub-fields that must be filled */
const REGISTERED_ADDRESS_KEYS = [
	"address1",
	"city",
	"stateProvince",
	"zip",
	"country",
] as const;

function getFieldValue(
	data: Record<string, unknown>,
	fieldId: string,
): unknown {
	const camel = data[fieldId];
	if (camel !== undefined && camel !== null) return camel;
	if (fieldId === "virtualAssetsDetails") return data.virtual_assets_info;
	const snake = fieldId.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`);
	return data[snake];
}

function isFilled(value: unknown, fieldId: string): boolean {
	if (value === undefined || value === null) return false;
	if (typeof value === "string") return value.trim() !== "";
	if (typeof value === "boolean") return true;
	if (typeof value === "number") return true;
	if (value instanceof Date) return !Number.isNaN(value.getTime());
	if (Array.isArray(value)) return value.length > 0;

	if (typeof value === "object") {
		const obj = value as Record<string, unknown>;

		if (CONTACT_FIELDS.has(fieldId)) {
			const name = obj.name;
			const email = obj.email;
			const phone = obj.phone;
			return (
				typeof name === "string" &&
				name.trim() !== "" &&
				typeof email === "string" &&
				email.trim() !== "" &&
				typeof phone === "string" &&
				phone.trim() !== ""
			);
		}

		if (fieldId === "registeredAddress") {
			return REGISTERED_ADDRESS_KEYS.every((k) => {
				const v = obj[k];
				return v != null && String(v).trim() !== "";
			});
		}

		return Object.values(obj).some((v) => v != null && String(v).trim() !== "");
	}
	return false;
}

function shouldApplyRule(
	rule: ValidationRule,
	formValues: Record<string, unknown>,
): boolean {
	if (!rule.appliesWhen) return true;
	return evaluateCondition(rule.appliesWhen, formValues);
}

/**
 * Validates formData against config-driven required fields.
 * Returns list of human-readable error messages for missing/invalid fields.
 */
export function validateFormForSubmit(
	formData: Record<string, unknown>,
): string[] {
	const errors: string[] = [];

	const country = String(
		formData.countryOfIncorporation ?? formData.country_of_incorporation ?? "",
	);
	const entity = String(formData.entityType ?? formData.entity_type ?? "");

	const ctx: FieldResolverContext = {
		incorporationCountry: country,
		entityType: entity,
		flowType: "KYB",
	};

	const resolved = resolveFieldConfig(ctx);
	const formValues = formData as Record<string, unknown>;
	const fieldsToValidate = filterByDependsOn(resolved.fields, formValues);

	for (const field of fieldsToValidate) {
		const value = getFieldValue(formData, field.fieldId);
		const label = field.label.en ?? field.fieldId;

		// Check config.required
		if (field.required && !isFilled(value, field.fieldId)) {
			errors.push(`${label} (Step ${field.step})`);
			continue;
		}

		if (isFilled(value, field.fieldId)) {
			// Field has a value — enforce format/length rules (max_length, regex,
			// email, url, min_length). Previously only `required` was checked here,
			// so a filled-but-invalid field (business description over the character
			// cap, a mis-formatted EIN, etc.) passed the client gate and was rejected
			// server-side with a generic "Form is incomplete" the partner couldn't act on.
			const ruleError = validateFieldValue(field, value, formValues);
			if (ruleError) {
				errors.push(`${label} — ${ruleError} (Step ${field.step})`);
			}
		} else {
			// Empty & optional — only conditional (appliesWhen) required rules apply
			// (e.g. enforcementDetails when regulatoryEnforcement is set).
			for (const rule of field.validationRules) {
				if (rule.type !== "required") continue;
				if (!shouldApplyRule(rule, formValues)) continue;
				errors.push(`${label} (Step ${field.step})`);
				break;
			}
		}
	}

	// Special: ownershipTiers — when present, each tier needs ownerType, percentage, etc.
	const rawTiers = formData.ownershipTiers ?? formData.ownership_data;
	const tiers = Array.isArray(rawTiers)
		? rawTiers
		: (rawTiers as Record<string, unknown> | undefined)?.tiers;
	if (Array.isArray(tiers) && tiers.length > 0) {
		const { thresholds } = resolved;
		const uboTrigger = thresholds.uboTrigger ?? 25;

		for (let i = 0; i < tiers.length; i++) {
			const tier = tiers[i] as Record<string, unknown>;
			const ownerType = tier.ownerType ?? tier.owner_type;
			const pct = tier.ownershipPercentage ?? tier.ownership_percentage;

			if (!ownerType || pct === undefined || pct === null) {
				errors.push(`Ownership tier ${i + 1} (Step 3)`);
				continue;
			}
			if (ownerType === "Corporation") {
				// Corporate tiers capture structured company fields + nested UBOs
				// (Step3Ownership writes `ultimateBeneficialOwners`). The legacy
				// `corporateOwnersList` string is never populated by the form, so
				// validating it falsely blocked every corporate tier.
				const corpFields = [
					tier.companyName ?? tier.company_name,
					tier.registrationNumber ?? tier.registration_number,
					tier.dateOfIncorporation ?? tier.date_of_incorporation,
					tier.registeredOfficeAddress ?? tier.registered_office_address,
					tier.taxId ?? tier.tax_id,
					tier.legalEntityType ?? tier.legal_entity_type,
				];
				const ubos =
					tier.ultimateBeneficialOwners ?? tier.ultimate_beneficial_owners;
				const uboList = Array.isArray(ubos) ? ubos : [];
				// Each nested UBO must carry the required fields the Step 3 form
				// marks for that owner type. UBOs below the trigger collect no
				// detail; nested UBOs never collect a photo ID (unlike the
				// top-level Individual branch below), so it is not required here.
				const uboIncomplete = uboList.some((raw) => {
					const ubo = raw as Record<string, unknown>;
					const uboType = ubo.ownerType ?? ubo.owner_type;
					const uboPct = ubo.ownershipPercentage ?? ubo.ownership_percentage;
					if (!uboType || uboPct === undefined || uboPct === null) return true;
					if (typeof uboPct === "number" && uboPct < uboTrigger) return false;
					const requiredFields =
						uboType === "Corporation"
							? [
									ubo.companyName ?? ubo.company_name,
									ubo.registrationNumber ?? ubo.registration_number,
									ubo.dateOfIncorporation ?? ubo.date_of_incorporation,
									ubo.registeredOfficeAddress ?? ubo.registered_office_address,
									ubo.taxId ?? ubo.tax_id,
									ubo.legalEntityType ?? ubo.legal_entity_type,
								]
							: [
									ubo.fullName ?? ubo.full_name,
									ubo.nationality,
									ubo.dateOfBirth ?? ubo.date_of_birth,
									ubo.countryOfResidence ?? ubo.country_of_residence,
									ubo.residentialAddress ?? ubo.residential_address,
									ubo.city,
									ubo.postalCode ?? ubo.postal_code,
								];
					return requiredFields.some((v) => !v || String(v).trim() === "");
				});
				if (
					corpFields.some((v) => !v || String(v).trim() === "") ||
					uboList.length === 0 ||
					uboIncomplete
				) {
					errors.push(`Ownership tier ${i + 1} corporate owners (Step 3)`);
				}
			}
			if (
				ownerType === "Individual" &&
				typeof pct === "number" &&
				pct >= uboTrigger
			) {
				const uboFields = [
					tier.fullName ?? tier.full_name,
					tier.dateOfBirth ?? tier.date_of_birth,
					tier.nationality,
					tier.residentialAddress ?? tier.residential_address,
					tier.photoIdUrl ?? tier.photo_id_url,
				];
				if (uboFields.some((v) => !v || String(v).trim() === "")) {
					errors.push(`Ownership tier ${i + 1} UBO info (Step 3)`);
				}
			}
		}
	}

	return errors;
}

/**
 * Validates a single field value against its config validationRules.
 * Returns the first error message or undefined if valid.
 * formValues is needed for appliesWhen conditions.
 */
export function validateFieldValue(
	config: FieldConfig,
	value: unknown,
	formValues?: Record<string, unknown>,
): string | undefined {
	for (const rule of config.validationRules) {
		if (
			formValues &&
			rule.appliesWhen &&
			!evaluateCondition(rule.appliesWhen, formValues)
		) {
			continue;
		}

		switch (rule.type) {
			case "required": {
				const filled =
					value !== undefined &&
					value !== null &&
					(typeof value !== "string" || value.trim() !== "") &&
					(typeof value !== "object" ||
						!Array.isArray(value) ||
						value.length > 0);
				if (!filled) return rule.message;
				break;
			}
			case "min_length": {
				const s = typeof value === "string" ? value : String(value ?? "");
				if (s.length < (rule.value as number)) return rule.message;
				break;
			}
			case "max_length": {
				const s = typeof value === "string" ? value : String(value ?? "");
				if (s.length > (rule.value as number)) return rule.message;
				break;
			}
			case "email": {
				const s = String(value ?? "").trim();
				if (s && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return rule.message;
				break;
			}
			case "url": {
				const s = String(value ?? "").trim();
				if (s && !/^https?:\/\/.+/.test(s)) return rule.message;
				break;
			}
			case "regex": {
				const s = String(value ?? "");
				if (s && rule.value && !new RegExp(rule.value as string).test(s))
					return rule.message;
				break;
			}
			case "min":
			case "max": {
				const n = Number(value);
				if (Number.isNaN(n)) break;
				const v = rule.value as number;
				if (rule.type === "min" && n < v) return rule.message;
				if (rule.type === "max" && n > v) return rule.message;
				break;
			}
			default:
				break;
		}
	}
	return undefined;
}
